import { BarTooltipProps } from '@nivo/bar';
import { ResponsiveBar } from '@nivo/bar';
import { FocusAreaDefinition } from 'libs/end-to-end/src/types/databases/focus-areas/focus-area-definition';
import { getTotalTestCount } from 'libs/end-to-end/src/tests-utils';
import { TestSummary } from 'libs/end-to-end/src/tests-utils';
import { getTestCountsByFocusArea } from 'libs/end-to-end/src/tests-utils';
import { getCalibrationLevel } from 'libs/end-to-end/src/calibration-level';
import { getCalibrationScore } from 'libs/end-to-end/src/tests-utils';
import React, { useMemo } from 'react';
import { KARLA_FONT_FAMILY } from 'libs/end-to-end/src/types/branding/fonts';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { CalibrationLevelPill } from 'apps/sigma2-ui/src/components/utils/CalibrationLevelPill';
import { getBarChartAxisLeft } from 'apps/sigma2-ui/src/copilot/components/OptimizationLevelBarChart';
import { CalibrationFraming } from 'libs/end-to-end/src/calibration-framing';
import { getCalibrationLevelUIDetails } from 'libs/end-to-end/src/ui';
import { adjustCalibrationScoreForGraph } from 'apps/sigma2-ui/src/lib/graph-helpers';
import { MyFocusAreaDefinition } from 'libs/end-to-end/src/my-focus-area-definition';

type BarChartDatum = {
  label: string;
  calibrationScore?: number;
  passingTests?: number;
  incompleteTests?: number;
  totalTests?: number;
};

const BarChartTooltip = ({
  datum,
  calibrationFraming,
}: {
  datum: BarTooltipProps<BarChartDatum>;
  calibrationFraming: CalibrationFraming;
}) => {
  const dimension = datum;
  const language = useLanguage();

  const { calibrationScore, passingTests, totalTests } = dimension.data;

  const calibrationLevel = getCalibrationLevel(calibrationScore ?? 0);

  return (
    <div className="filter shadow-8px bg-white text-left p-4 font-sans bg-opacity-70 backdrop-blur-sm rounded-md">
      <p className="text-xl font-bold font-sans">{dimension.data.label}</p>
      <CalibrationLevelPill
        calibrationLevel={calibrationLevel}
        calibrationFraming={calibrationFraming}
      />

      <p>
        {language.polarChartTooltipTestStatement({
          passingTests: passingTests ?? 0,
          totalTests: totalTests ?? 0,
        })}
      </p>
    </div>
  );
};

export const OverviewFocusAreaBarChart = ({
  testSummary,
  focusAreas,
  calibrationFraming,
}: {
  testSummary: TestSummary | null;
  focusAreas: MyFocusAreaDefinition[] | null;
  calibrationFraming: CalibrationFraming;
}) => {
  const language = useLanguage();

  const data = useMemo(() => {
    const dataPoints: BarChartDatum[] = [];

    if (!focusAreas || !testSummary) {
      return dataPoints;
    }

    for (const focusArea of focusAreas) {
      const counts = getTestCountsByFocusArea(
        testSummary.tests,
        focusArea.focusArea,
      );

      if (!counts) {
        dataPoints.push({
          label: focusArea.displayName,
          [`${focusArea.displayName}`]: 0,
          calibrationScore: 0,
          passingTests: 0,
          incompleteTests: 0,
          totalTests: 0,
        });

        continue;
      }

      const calibrationScore = getCalibrationScore({
        testCount: counts,
      });

      dataPoints.push({
        label: focusArea.displayName,
        [`${focusArea.displayName}`]: calibrationScore,
        calibrationScore: calibrationScore,
        passingTests: counts.passing,
        incompleteTests: counts.incomplete,
        totalTests: getTotalTestCount(counts),
      });
    }
    return dataPoints;
  }, [focusAreas, testSummary]);

  const keys = useMemo(() => {
    return focusAreas?.map((focusArea) => focusArea.displayName) ?? [];
  }, [focusAreas]);

  const barChartColors: string[] = useMemo(() => {
    return (
      data.map((result) => {
        const calibrationScore = result.calibrationScore;
        const calibrationLevel = getCalibrationLevel(calibrationScore ?? 0);
        const uiDetails =
          getCalibrationLevelUIDetails(calibrationFraming)[calibrationLevel];
        return uiDetails.hexColor;
      }) || []
    );
  }, [data, calibrationFraming]);

  return (
    <div className="aspect-square relative w-full h-[90%]">
      <ResponsiveBar
        data={data.map((datum) => ({
          ...datum,
          [datum.label]: adjustCalibrationScoreForGraph(
            datum.calibrationScore ?? 0,
            calibrationFraming,
          ),
        }))}
        groupMode="stacked"
        margin={{ top: 10, right: 50, bottom: 100, left: 100 }}
        minValue={0}
        maxValue={6}
        borderRadius={3}
        colors={barChartColors}
        colorBy="id"
        label=""
        padding={0.4}
        innerPadding={3}
        keys={keys}
        indexBy={'label'}
        enableGridX={false}
        axisLeft={getBarChartAxisLeft({ language, calibrationFraming })}
        tooltip={(datum) => (
          <BarChartTooltip
            datum={datum}
            calibrationFraming={calibrationFraming}
          />
        )}
        axisBottom={{
          tickPadding: 8,
          renderTick: (tick) => {
            return (
              <g transform={`translate(${tick.x},${tick.y + 22})`}>
                <text
                  x={-25}
                  y={15}
                  textAnchor="before"
                  transform="rotate(25)"
                  style={{ fontSize: 16, fontWeight: 'bold', color: '#3B4046' }}
                >
                  {tick.value}
                </text>
              </g>
            );
          },
        }}
        theme={{
          text: {
            fontFamily: KARLA_FONT_FAMILY,
            fontSize: 16,
          },
          axis: {
            domain: {
              line: {
                stroke: '#B8BABC',
                strokeWidth: 2,
              },
            },
            ticks: {
              line: {
                strokeWidth: 0,
              },
            },
            legend: {
              text: {
                stroke: '#791fd1',
                fontSize: 16,
                strokeWidth: 0.2,
              },
            },
          },
        }}
      />
    </div>
  );
};
